import { useEffect, useRef, type MutableRefObject } from 'react';

import { useConsumer } from '../lib/channels/hooks/use-consumer';

/**
 * Event types received from BizyChannel on the backend.
 * All events may include an optional request_id for multi-tab isolation.
 */
export interface BizyTokenEvent {
	event: 'token';
	data: { content: string; request_id?: string };
}

export interface BizyCompleteEvent {
	event: 'complete';
	data: {
		content: string;
		message_id: string | null;
		conversation_id: string;
		emotion: string;
		tools_used: string[];
		response_time_ms: number;
		first_token_ms: number | null;
		request_id?: string;
		connector_processing?: boolean;
		connector_response?: boolean;
	};
}

export interface BizyActionEvent {
	event: 'page_action';
	data: {
		action: string;
		report_type?: string;
		page_path?: string;
		params?: Record<string, unknown>;
		report_view?: string;
		celebration_id?: string;
		request_id?: string;
		conversation_id?: string;
	};
}

export interface BizyErrorEvent {
	event: 'error';
	data: { message: string; request_id?: string };
}

export interface BizyToolCallEvent {
	event: 'tool_call';
	data: { tool_name: string; arguments: Record<string, unknown>; request_id?: string };
}

export interface BizyPreliminaryTokenEvent {
	event: 'preliminary_token';
	data: { content: string; request_id?: string };
}

export interface BizyCancelledEvent {
	event: 'cancelled';
	data: { content: string; request_id?: string };
}

export interface BizySupportHandoffEvent {
	event: 'support_handoff';
	data: { category?: string; summary?: string; fin_conversation_id?: string; request_id?: string };
}

export interface BizySupportResolvedEvent {
	event: 'support_resolved';
	data: { fin_conversation_id?: string; request_id?: string };
}

export interface BizySupportEscalatedEvent {
	event: 'support_escalated';
	data: { intercom_conversation_id?: string; request_id?: string };
}

// ─── AssistantMessage node events ─────────────────────────────────────────────
// The conductor emits these as it produces content. Together they let the
// frontend render the assistant message incrementally as a typed Document.

/** Incremental delta for an open markdown node. `node_id` groups deltas; `seq`
 *  is monotonic across all node events in a turn for ordered application. */
export interface BizyNodeTokenEvent {
	event: 'node_token';
	data: {
		content: string;
		node_id: number;
		seq: number;
		conversation_id?: string;
		request_id?: string;
	};
}

/** Complete card or one-shot markdown node. Closes any currently-open markdown
 *  node and appends a fresh one with this node_id at the document tail.
 *  Markdown payloads carry `{content}`; card payloads carry the existing
 *  card-shaped fields (camelCase). */
export interface BizyNodeAppendedEvent {
	event: 'node_appended';
	data: {
		type:
			| 'markdown'
			| 'follow_ups'
			| 'scorecard'
			| 'chart'
			| 'matrix'
			| 'table'
			| 'source_details'
			| 'approval_card'
			| 'content_card'
			| 'draft_card'
			| 'meeting_people_card'
			| 'recognition_draft_card'
			| 'comment_draft_card'
			| 'recognition_view_card'
			| 'user_admin_batch_confirm_card';
		payload: Record<string, unknown>;
		node_id: number;
		seq: number;
		conversation_id?: string;
		request_id?: string;
	};
}

export type BizyChannelEvent =
	| BizyTokenEvent
	| BizyCompleteEvent
	| BizyErrorEvent
	| BizyToolCallEvent
	| BizyPreliminaryTokenEvent
	| BizyActionEvent
	| BizyCancelledEvent
	| BizySupportHandoffEvent
	| BizySupportResolvedEvent
	| BizySupportEscalatedEvent
	| BizyNodeTokenEvent
	| BizyNodeAppendedEvent;

export interface UseBizyChannelOptions {
	onToken?: (content: string) => void;
	onComplete?: (data: BizyCompleteEvent['data']) => void;
	onError?: (message: string) => void;
	onToolCall?: (data: BizyToolCallEvent['data']) => void;
	onPreliminaryToken?: (content: string) => void;
	onCancelled?: (content: string) => void;
	onPageAction?: (data: BizyActionEvent['data']) => void;
	onSupportHandoff?: (data: BizySupportHandoffEvent['data']) => void;
	onSupportResolved?: (data: BizySupportResolvedEvent['data']) => void;
	onSupportEscalated?: (data: BizySupportEscalatedEvent['data']) => void;
	/** Incremental delta for an open markdown node on the typed
	 *  assistant_message event stream. */
	onNodeToken?: (data: BizyNodeTokenEvent['data']) => void;
	/** Complete card or one-shot markdown node on the typed
	 *  assistant_message event stream. */
	onNodeAppended?: (data: BizyNodeAppendedEvent['data']) => void;
	onConnected?: () => void;
	onDisconnected?: () => void;
	/** Fires when another tab's request completes (cross-tab sync). */
	onOtherTabComplete?: (data: BizyCompleteEvent['data']) => void;
	/** Fires when another tab is actively streaming (cross-tab awareness). */
	onOtherTabLoading?: () => void;
	/** Fires when another tab's request errors (cross-tab sync — clears loading). */
	onOtherTabError?: () => void;
	/** Ref to the active request ID. Read synchronously in the WebSocket handler to avoid React state batching delays. */
	activeRequestIdRef?: MutableRefObject<string | undefined>;
}

/**
 * Hook for subscribing to BizyChannel via ActionCable.
 *
 * Events matching `activeRequestIdRef.current` (or without a request_id) are
 * routed to the primary callbacks. Events from a different request_id are
 * routed to `onOtherTabComplete` / `onOtherTabLoading` for cross-tab sync.
 */
export function useBizyChannel(userId: string | undefined, options: UseBizyChannelOptions) {
	const consumer = useConsumer();
	const subscriptionRef = useRef<any>(null);

	const optionsRef = useRef(options);
	optionsRef.current = options;

	useEffect(() => {
		if (!consumer || !userId) return;

		const timer = setTimeout(() => {
			const subscription = consumer.subscriptions.create(
				{ channel: 'BizyChannel' },
				{
					connected: () => {
						optionsRef.current.onConnected?.();
					},
					disconnected: () => {
						optionsRef.current.onDisconnected?.();
					},
					rejected: () => {
						optionsRef.current.onDisconnected?.();
					},
					received: (message: BizyChannelEvent) => {
						if (!message) return;

						const activeId = optionsRef.current.activeRequestIdRef?.current;
						const eventRequestId = 'request_id' in message.data ? message.data.request_id : undefined;
						const isOtherTab = activeId && eventRequestId ? eventRequestId !== activeId : false;

						const isSupportEvent = message.event === 'support_handoff' || message.event === 'support_resolved' || message.event === 'support_escalated';

						if (isOtherTab && !isSupportEvent) {
							if (message.event === 'complete') {
								optionsRef.current.onOtherTabComplete?.(message.data);
							} else if (message.event === 'error' || message.event === 'cancelled') {
								optionsRef.current.onOtherTabError?.();
							} else if (message.event === 'token' || message.event === 'tool_call' || message.event === 'node_token' || message.event === 'node_appended') {
								optionsRef.current.onOtherTabLoading?.();
							}
							return;
						}

						switch (message.event) {
							case 'token':
								optionsRef.current.onToken?.(message.data.content);
								break;
							case 'complete':
								optionsRef.current.onComplete?.(message.data);
								break;
							case 'error':
								optionsRef.current.onError?.(message.data.message);
								break;
							case 'tool_call':
								optionsRef.current.onToolCall?.(message.data);
								break;
							case 'preliminary_token':
								optionsRef.current.onPreliminaryToken?.(message.data.content);
								break;
							case 'cancelled':
								optionsRef.current.onCancelled?.(message.data.content);
								break;
							case 'page_action':
								optionsRef.current.onPageAction?.(message.data);
								break;
							case 'support_handoff':
								optionsRef.current.onSupportHandoff?.(message.data);
								break;
							case 'support_resolved':
								optionsRef.current.onSupportResolved?.(message.data);
								break;
							case 'support_escalated':
								optionsRef.current.onSupportEscalated?.(message.data);
								break;
							case 'node_token':
								optionsRef.current.onNodeToken?.(message.data);
								break;
							case 'node_appended':
								optionsRef.current.onNodeAppended?.(message.data);
								break;
						}
					},
				}
			);

			subscriptionRef.current = subscription;
		}, 0);

		return () => {
			clearTimeout(timer);
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
		};
	}, [consumer, userId]);
}
